/**
 * Wrapper su RTCPeerConnection per la chiamata di supporto 1-a-1.
 *
 * Il media e' peer-to-peer: non passa dal nostro backend, che vede solo i messaggi
 * di signaling opachi passati a `onSignal`.
 */

export interface SupportPeerHandlers {
  onRemoteStream: (stream: MediaStream) => void;
  onSignal: (payload: unknown) => void;
  onFailed: () => void;
  /** La condivisione dello schermo e' finita da sola: l'utente l'ha fermata dal
   *  banner nativo del browser, non dal nostro pulsante. Senza questa notifica lo
   *  stato nella UI resta "sto condividendo" e il pulsante mente. */
  onScreenEnded: () => void;
}

/** L'utente ha annullato il selettore di condivisione, o il browser l'ha negata.
 *  Firefox solleva NotAllowedError con "The request is not allowed by the user
 *  agent or the platform in the current context": non e' un errore da log, e' una
 *  scelta dell'utente, e va detta con parole sue. */
export class CondivisioneNegata extends Error {
  constructor() {
    super("condivisione_negata");
  }
}

/** Ne' webcam ne' microfono disponibili: la chiamata non puo' partire. */
export class MediaBloccato extends Error {
  constructor() {
    super("media_bloccato");
  }
}

export class SupportPeer {
  private pc: RTCPeerConnection;
  private local: MediaStream | null = null;
  private screen: MediaStream | null = null;
  private camTrack: MediaStreamTrack | null = null;
  /** Candidate ICE arrivati prima della remote description: si accodano e si applicano dopo. */
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private handlers: SupportPeerHandlers;

  constructor(iceServers: RTCIceServer[], handlers: SupportPeerHandlers) {
    this.handlers = handlers;
    this.pc = new RTCPeerConnection({ iceServers });
    this.pc.onicecandidate = (e) => {
      if (e.candidate) this.handlers.onSignal({ candidate: e.candidate });
    };
    this.pc.ontrack = (e) => {
      const [stream] = e.streams;
      if (stream) this.handlers.onRemoteStream(stream);
    };
    this.pc.oniceconnectionstatechange = () => {
      if (this.pc.iceConnectionState === "failed") this.handlers.onFailed();
    };
  }

  /**
   * Acquisisce webcam+microfono. Se il video e' negato riprova con solo audio:
   * una chiamata di supporto con solo voce e schermo e' comunque utile.
   * Rilancia `MediaBloccato` se non si ottiene nemmeno l'audio.
   */
  async startLocalMedia(): Promise<MediaStream> {
    try {
      this.local = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    } catch {
      try {
        this.local = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch {
        throw new MediaBloccato();
      }
    }
    for (const track of this.local.getTracks()) {
      this.pc.addTrack(track, this.local);
      if (track.kind === "video") this.camTrack = track;
    }
    return this.local;
  }

  /** True se la webcam non e' disponibile (permesso negato o assente). */
  get videoDisponibile(): boolean {
    return this.camTrack !== null;
  }

  async createOffer(): Promise<void> {
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.handlers.onSignal({ sdp: offer });
  }

  async handleSignal(payload: unknown): Promise<void> {
    const msg = payload as { sdp?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit };
    if (msg.sdp) {
      await this.pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      // Ora che la remote description e' impostata, i candidate accodati nel
      // frattempo si possono applicare senza incorrere in InvalidStateError.
      for (const c of this.pendingCandidates.splice(0)) {
        await this.pc.addIceCandidate(c);
      }
      if (msg.sdp.type === "offer") {
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.handlers.onSignal({ sdp: answer });
      }
      return;
    }
    if (msg.candidate) {
      // Per spec WebRTC, addIceCandidate() prima della remote description fallisce:
      // il trasporto WS non garantisce che l'SDP arrivi sempre per primo.
      if (this.pc.remoteDescription) {
        await this.pc.addIceCandidate(msg.candidate);
      } else {
        this.pendingCandidates.push(msg.candidate);
      }
    }
  }

  /**
   * Attiva/disattiva la condivisione schermo. Ritorna lo stato risultante.
   *
   * ponytail: se esiste gia' una sender video (webcam presente) si usa
   * `replaceTrack` — lo schermo SOSTITUISCE la webcam, quindi non serve
   * renegoziazione. Se un giorno servisse mostrare cam e schermo insieme,
   * aggiungere una seconda sender e renegoziare sempre (non solo nel ramo sotto).
   *
   * Se la webcam era stata negata (nessuna sender video esistente, vedi
   * `startLocalMedia`), lo schermo e' comunque utile: si aggiunge una sender
   * nuova con `addTrack` e si rinegozia con `createOffer()` — il peer remoto
   * gestisce gia' un'offerta in arrivo rispondendo con una answer.
   */
  async toggleScreen(): Promise<boolean> {
    const sender = this.videoSender();

    if (this.screen) {
      for (const t of this.screen.getTracks()) t.stop();
      this.screen = null;
      // Se non c'era webcam (camTrack null) questo pulisce la sender senza
      // lasciare un riferimento al track di schermo ormai fermato.
      if (sender) await sender.replaceTrack(this.camTrack);
      return false;
    }

    try {
      this.screen = await navigator.mediaDevices.getDisplayMedia({ video: true });
    } catch (err) {
      // NotAllowedError = picker annullato o permesso negato (il caso comune, non
      // un guasto). Qualunque altro errore resta un errore e viene propagato.
      if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError")) {
        throw new CondivisioneNegata();
      }
      throw err;
    }
    const [screenTrack] = this.screen.getVideoTracks();
    // L'utente puo' fermare la condivisione dal banner nativo del browser: qui la
    // spegniamo e avvisiamo la UI, che altrimenti resterebbe con il pulsante attivo.
    screenTrack.onended = () => {
      void this.toggleScreen().then(() => this.handlers.onScreenEnded());
    };

    if (sender) {
      await sender.replaceTrack(screenTrack);
    } else {
      this.pc.addTrack(screenTrack, this.screen);
      await this.createOffer();
    }
    return true;
  }

  setMicEnabled(on: boolean): void {
    for (const t of this.local?.getAudioTracks() ?? []) t.enabled = on;
  }

  setCamEnabled(on: boolean): void {
    if (this.camTrack) this.camTrack.enabled = on;
  }

  close(): void {
    for (const t of this.local?.getTracks() ?? []) t.stop();
    for (const t of this.screen?.getTracks() ?? []) t.stop();
    this.local = null;
    this.screen = null;
    this.camTrack = null;
    this.pendingCandidates = [];
    this.pc.close();
  }

  private videoSender(): RTCRtpSender | undefined {
    return this.pc.getSenders().find((s) => s.track?.kind === "video" || s.track === null);
  }
}
